/**
 * The 26-letter alphabet the classical ciphers work over, and the text handling every
 * one of them shares.
 *
 * Classical ciphers are defined on A–Z, and two decisions follow from that. They are
 * made once, here, so every cipher in the family answers them the same way:
 *
 * - **Accented Latin letters are enciphered as their base letter** (é → e, Ç → C). A
 *   clerk enciphering French wrote without accents, and leaving "é" untouched would put
 *   a plaintext letter in the middle of the cipher text.
 * - **Letters from other alphabets** (ß, æ, Cyrillic, Greek, Devanagari…) belong to none
 *   of these ciphers. `foreignLetters` finds them so a widget can say what it did with
 *   them, rather than handing back text that looks enciphered and partly is not.
 */

export const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
export const ALPHABET_SIZE = 26;

const UPPER_A = 65;
const UPPER_Z = 90;
const LOWER_A = 97;
const LOWER_Z = 122;
const ASCII_LIMIT = 128;

const COMBINING_MARKS = /^\p{M}+$/u;
const ANY_LETTER = /\p{L}/u;
const LETTER_OR_MARK = /[\p{L}\p{M}]/u;
const WHITESPACE = /\s/u;

/** Euclidean modulo: −1 mod 26 is 25, not −1. */
export function mod(value: number, modulus: number): number {
  return ((value % modulus) + modulus) % modulus;
}

/** 0–25 for A–Z or a–z; −1 for anything else. */
export function letterIndex(char: string): number {
  if (char.length !== 1) {
    return -1;
  }
  const code = char.charCodeAt(0);
  if (code >= UPPER_A && code <= UPPER_Z) {
    return code - UPPER_A;
  }
  if (code >= LOWER_A && code <= LOWER_Z) {
    return code - LOWER_A;
  }
  return -1;
}

export function isLowerCaseLetter(char: string): boolean {
  const code = char.charCodeAt(0);
  return char.length === 1 && code >= LOWER_A && code <= LOWER_Z;
}

/** The capital letter at an index, wrapped into 0–25. */
export function letterAt(index: number): string {
  return ALPHABET.charAt(mod(index, ALPHABET_SIZE));
}

function foldChar(char: string): string {
  if (char.charCodeAt(0) < ASCII_LIMIT) {
    return char;
  }
  const decomposed = char.normalize("NFD");
  const base = decomposed.charAt(0);
  if (letterIndex(base) >= 0 && COMBINING_MARKS.test(decomposed.slice(1))) {
    return base;
  }
  return char;
}

/**
 * Accented Latin letters to their base letter: "Café Noël" → "Cafe Noel".
 *
 * Only a character whose canonical decomposition is an A–Z letter plus combining marks
 * is folded. Stripping marks wholesale would mangle the scripts that pass through
 * untouched — Devanagari's vowel signs are combining marks too.
 */
export function foldDiacritics(text: string): string {
  let folded = "";
  for (const char of text.normalize("NFC")) {
    folded += foldChar(char);
  }
  return folded;
}

/**
 * Letters that are still outside A–Z after folding, each listed once in the order they
 * first appear, with the total count.
 */
export function foreignLetters(text: string): {
  count: number;
  letters: string[];
} {
  const seen = new Set<string>();
  let count = 0;
  for (const char of foldDiacritics(text)) {
    if (ANY_LETTER.test(char) && letterIndex(char) < 0) {
      count += 1;
      seen.add(char);
    }
  }
  return { count, letters: [...seen] };
}

/** The A–Z letters of a text in capitals, accents folded; everything else dropped. */
export function lettersOnly(text: string): string {
  let letters = "";
  for (const char of foldDiacritics(text)) {
    const index = letterIndex(char);
    if (index >= 0) {
      letters += ALPHABET.charAt(index);
    }
  }
  return letters;
}

/**
 * Run every A–Z letter through `transform` (an index → index map), keeping its case.
 * Every other character is returned unchanged. Accents are folded first.
 */
export function mapLetters(
  text: string,
  transform: (index: number) => number
): string {
  let result = "";
  for (const char of foldDiacritics(text)) {
    const index = letterIndex(char);
    if (index < 0) {
      result += char;
      continue;
    }
    const mapped = letterAt(transform(index));
    result += isLowerCaseLetter(char) ? mapped.toLowerCase() : mapped;
  }
  return result;
}

/**
 * A keyword-mixed alphabet: the keyword's letters in order, each once, then the rest of
 * `alphabet` in order.
 *
 * `normalize` maps a keyword letter onto `alphabet` — J → I for a square that merges
 * them — and a letter it maps outside `alphabet` is skipped (Q, for a square that drops
 * Q).
 */
export function keywordAlphabet(
  keyword: string,
  alphabet: string,
  normalize: (letter: string) => string = (letter) => letter
): string {
  const used = new Set<string>();
  let mixed = "";
  for (const letter of `${lettersOnly(keyword)}${alphabet}`) {
    const mapped = normalize(letter);
    if (mapped.length === 1 && alphabet.includes(mapped) && !used.has(mapped)) {
      used.add(mapped);
      mixed += mapped;
    }
  }
  return mixed;
}

/** What the keep/strip checkboxes of a cipher workbench decide. */
export interface CipherTextOptions {
  /** Off: letters come out in capitals. */
  keepCase: boolean;
  /** Off: spaces and line breaks are removed. */
  keepSpaces: boolean;
  /** Off: punctuation, digits and symbols are removed. */
  keepPunctuation: boolean;
}

export const KEEP_ALL_TEXT: CipherTextOptions = {
  keepCase: true,
  keepSpaces: true,
  keepPunctuation: true,
};

/**
 * Apply the keep/strip options to a text.
 *
 * Letters (of any script) always stay. Whitespace is "spaces"; everything that is
 * neither a letter nor whitespace — punctuation, digits, symbols — is "punctuation".
 * A substitution cipher can apply this before or after enciphering with the same
 * result; a transposition cipher must apply it before, because what is kept is what
 * gets moved.
 */
export function applyTextOptions(
  text: string,
  options: CipherTextOptions
): string {
  let result = "";
  for (const char of text) {
    if (WHITESPACE.test(char)) {
      if (options.keepSpaces) {
        result += char;
      }
    } else if (LETTER_OR_MARK.test(char)) {
      result += options.keepCase ? char : char.toUpperCase();
    } else if (options.keepPunctuation) {
      result += char;
    }
  }
  return result;
}

/** "ABCDEFGH" → "ABCDE FGH": cipher text in the traditional blocks. */
export function groupLetters(text: string, size: number): string {
  const chars = [...text];
  const groups: string[] = [];
  for (let start = 0; start < chars.length; start += size) {
    groups.push(chars.slice(start, start + size).join(""));
  }
  return groups.join(" ");
}
